import styles from './ManagementProfileTab.module.scss';
import useManagementProfileTabLogic from './useManagementProfileTabLogic';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import userImg from '../../../../assets/user.png';
import clsx from 'clsx';
import { toTitleCase } from '../../../../utils/utils';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import ManagementUserProfile from '../Overview/components/ManagementProfile/components/ManagementUserProfile';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { alertIcon, ctaPurpleRight, noDataFoundEmptyState } from '../../../../assets/hostedassets';
import greenTick from '../../../../assets/green-tick.svg';
import greyLock from '../../../../assets/grey-lock-2.svg';
import { LOCKED_COLUMN_TOOLTIPS, MANAGEMENT_PROFILE_HEADERS } from './ManagementProfileTabConstants';
import { DATE_FORMATS, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import { TabModule } from '../../../../utils/constants/commonConstants';
import McaRefreshLoader, { McaDocData } from '../../../../common/McaRefreshLoader/McaRefreshLoader';
import { BureauType } from '../CreditBureau/components/Report/CreditBureauConstants';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_MANAGEMENT_DIRECTOR_VIEWED } from '../../../../utils/constants/mixpanelEvents/investorEvents';

interface ManagementProfileTabProps {
    navigateToTab?: (tabId: string, additionalParams?: Record<string, string>) => void;
    mcaDocData?: McaDocData | null;
}

function ManagementProfileTab({ navigateToTab, mcaDocData }: ManagementProfileTabProps) {
    const { overviewLoading, directors, columnLocks, selectedDirector, setSelectedDirector, lastUpdatedAt } =
        useManagementProfileTabLogic();
    const userData = useSelector((state: any) => state?.user?.userData);
    const params = useParams();

    const handleDirectorClick = (row: any) => {
        Mixpanel.track(AICA_MANAGEMENT_DIRECTOR_VIEWED, {
            parentId: userData?.investorOrgId,
            companyOrgId: params?.id,
            websiteUrl: window.location.href,
        });
        setSelectedDirector(row);
    };

    return (
        <div className={styles.ManagementProfileTab}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.heading}>Management Profile</h2>
                </div>
                <div className={styles.headerRight}>
                    <McaRefreshLoader mcaDocData={mcaDocData} />
                    {lastUpdatedAt && (
                        <span className={styles.lastUpdated}>
                            Last updated on {formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}
                        </span>
                    )}
                    <div className={styles.dataSourceBadge}>
                        <span className={styles.dataSourceBadgeLabel}>Source:</span>
                        <span className={styles.dataSourceBadgeValue}>MCA</span>
                    </div>
                </div>
            </div>
            <div className={styles.body}>
                {overviewLoading ? (
                    <LoadingSpinner color={'var(--primary-purple)'} height={'2.5rem'} style={{ margin: '3.75rem 0' }} />
                ) : directors.length === 0 ? (
                    <div className={styles.emptyState}>
                        <img src={noDataFoundEmptyState} alt="" className={styles.emptyStateIcon} />
                        <div className={styles.emptyStateTitle}>No data available</div>
                    </div>
                ) : (
                    <div className={styles.content}>
                        <div className={styles.table}>
                            <div className={styles.tableHeader}>
                                {MANAGEMENT_PROFILE_HEADERS.map(({ label, lockKey }) => {
                                    const locked = lockKey ? columnLocks[lockKey] : false;
                                    if (!locked || !lockKey) return <div key={label}>{label}</div>;
                                    return (
                                        <div key={label}>
                                            <Tooltip
                                                content={LOCKED_COLUMN_TOOLTIPS[lockKey]}
                                                direction="top"
                                                usePortal
                                                style={{ width: '240px', color: 'var(--icon-color)' }}
                                            >
                                                <span className={styles.lockedHeaderInner}>
                                                    {label}
                                                    <img src={greyLock} alt="" className={styles.lockedHeaderIcon} />
                                                </span>
                                            </Tooltip>
                                        </div>
                                    );
                                })}
                            </div>

                            {directors.map((row, idx) => (
                                <div
                                    key={`${row.din}-${idx}`}
                                    className={clsx(styles.tableRow, styles.clickableRow)}
                                    onClick={() => handleDirectorClick(row)}
                                >
                                    <div className={styles.nameCell}>
                                        <div className={styles.avatar}>
                                            <img
                                                src={row.imageUrl || userImg}
                                                alt={row.name}
                                                onError={({ currentTarget }) => {
                                                    currentTarget.onerror = null;
                                                    currentTarget.src = userImg;
                                                }}
                                            />
                                        </div>
                                        <div className={styles.nameInfo}>
                                            <span className={styles.directorName}>{toTitleCase(row.name)}</span>
                                            {row.linkedinUrl && row.followersCount && (
                                                <a
                                                    href={row.linkedinUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.linkedInLink}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span className={styles.followersCount}>{row.followersCount}</span>
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.cellText}>
                                        {row.designation ? (
                                            <span className={styles.cellTextValue} title={row.designation}>
                                                {row.designation}
                                            </span>
                                        ) : (
                                            <span className={styles.cellDash}>-</span>
                                        )}
                                    </div>

                                    <div>
                                        {row.tenureBeginDate ? (
                                            <div className={styles.tenureInfo}>
                                                <span className={styles.tenureYears}>
                                                    {formatTenureYears(row.tenureBeginDate, row.tenureEndDate)}
                                                </span>
                                                <span className={styles.tenureDates}>
                                                    ({formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} -{' '}
                                                    {row.tenureEndDate
                                                        ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR)
                                                        : row.isActive
                                                        ? 'Present'
                                                        : ''}
                                                    )
                                                </span>
                                            </div>
                                        ) : (
                                            <span className={styles.cellDash}>-</span>
                                        )}
                                    </div>

                                    <div>
                                        {row.loading ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.email || row.phone ? (
                                            <div className={styles.contactInfo}>
                                                {row.email && <span className={styles.contactEmail}>{row.email}</span>}
                                                {row.phone && <span className={styles.contactPhone}>{row.phone}</span>}
                                            </div>
                                        ) : (
                                            <span className={styles.cellDash}>-</span>
                                        )}
                                    </div>

                                    <div
                                        className={clsx({
                                            [styles.alertClickable]: !columnLocks.riskAlerts && !row.loading && !!row.riskIndicatorCount,
                                        })}
                                        onClick={
                                            !columnLocks.riskAlerts && !row.loading && row.riskIndicatorCount
                                                ? (e) => {
                                                      e.stopPropagation();
                                                      navigateToTab?.(TabModule.RELATED_PARTY, {
                                                          directorName: row.name ?? '',
                                                          directorDin: row.din ?? '',
                                                      });
                                                  }
                                                : undefined
                                        }
                                    >
                                        {columnLocks.riskAlerts ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.loading ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.riskIndicatorCount == null ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.riskIndicatorCount > 0 ? (
                                            <span className={styles.alertRed}>
                                                <img src={alertIcon} alt="" height="14px" />
                                                {`${row.riskIndicatorCount} ${row.riskIndicatorCount === 1 ? 'Alert' : 'Alerts'}`}
                                            </span>
                                        ) : (
                                            <span className={styles.alertGrey}>
                                                <img src={greenTick} alt="" height="14px" />
                                                No Alerts
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        className={clsx({
                                            [styles.alertClickable]: !columnLocks.regulatoryChecks && !row.loading && !!row.amlPepRiskCount,
                                        })}
                                        onClick={
                                            !columnLocks.regulatoryChecks && !row.loading && row.amlPepRiskCount
                                                ? (e) => {
                                                      e.stopPropagation();
                                                      navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                                          directorName: row.name ?? '',
                                                          directorDin: row.din ?? '',
                                                          section: 'regulatoryChecks',
                                                      });
                                                  }
                                                : undefined
                                        }
                                    >
                                        {columnLocks.regulatoryChecks ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.loading ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.amlPepRiskCount == null ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.amlPepRiskCount > 0 ? (
                                            <span className={styles.alertRed}>
                                                <img src={alertIcon} alt="" height="14px" />
                                                {`${row.amlPepRiskCount} ${row.amlPepRiskCount === 1 ? 'Indicator' : 'Indicators'}`}
                                            </span>
                                        ) : (
                                            <span className={styles.alertGrey}>
                                                <img src={greenTick} alt="" height="14px" />
                                                No Indicator
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        className={clsx({
                                            [styles.alertClickable]: !columnLocks.legalCases && !row.loading && !!row.legalCasesCount,
                                        })}
                                        onClick={
                                            !columnLocks.legalCases && !row.loading && row.legalCasesCount
                                                ? (e) => {
                                                      e.stopPropagation();
                                                      navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                                          directorName: row.name ?? '',
                                                          directorDin: row.din ?? '',
                                                      });
                                                  }
                                                : undefined
                                        }
                                    >
                                        {columnLocks.legalCases ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.loading ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : row.legalCasesCount == null || row.legalCasesCount === 0 ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : (
                                            <span className={styles.alertRed}>
                                                <img src={alertIcon} alt="" height="14px" />
                                                {`${row.legalCasesCount} ${row.legalCasesCount === 1 ? 'Case' : 'Cases'}`}
                                            </span>
                                        )}
                                    </div>

                                    {(() => {
                                        const isCreditClickable =
                                            !row.loading && typeof row.creditScore === 'number' && !Number.isNaN(row.creditScore);
                                        return (
                                            <div
                                                className={clsx({ [styles.creditClickable]: isCreditClickable })}
                                                onClick={
                                                    isCreditClickable
                                                        ? (e) => {
                                                              e.stopPropagation();
                                                              navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                                                  bureauType: BureauType.ALL,
                                                                  directorName: row.name ?? '',
                                                                  focusDirectorId: row.directorId ?? '',
                                                              });
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {isCreditClickable ? (
                                                    <span className={styles.creditScore}>
                                                        <img src={ctaPurpleRight} alt="" height="14px" />
                                                        {row.creditScore}
                                                    </span>
                                                ) : (
                                                    <span className={styles.cellDash}>-</span>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <div>
                                        {row.isActive === null || row.isActive === undefined ? (
                                            <span className={styles.cellDash}>-</span>
                                        ) : (
                                            <span
                                                className={clsx(
                                                    styles.statusBadge,
                                                    row.isActive ? styles.statusActive : styles.statusFormer,
                                                )}
                                            >
                                                {row.isActive ? 'Active' : 'Former'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {selectedDirector && (
                <SideDrawer onClose={() => setSelectedDirector(null)} width={'60%'}>
                    <ManagementUserProfile
                        showUserProfile={selectedDirector}
                        dataPreloaded
                        redirectToCreditBureau={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.CREDIT_BUREAU, {
                                bureauType: BureauType.ALL,
                                directorName: director?.name ?? '',
                                focusDirectorId: director?.directorId ?? '',
                            });
                        }}
                        redirectToCompliances={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.LEGAL_CHECKS, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                        redirectToRiskIndicators={() => {
                            const director = selectedDirector;
                            setSelectedDirector(null);
                            navigateToTab?.(TabModule.RELATED_PARTY, {
                                directorName: director?.name ?? '',
                                directorDin: director?.din ?? '',
                            });
                        }}
                    />
                </SideDrawer>
            )}
        </div>
    );
}

export default ManagementProfileTab;
